import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Coffee, PartyPopper, Users, Map, Trophy, Check, X, ChevronRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

// The 5 "Aha moment" first-week actions. `to` is where the CTA takes them;
// `key` maps to the backend onboarding flags (coffee/explore complete on tap).
const STEPS = [
  { key: "coffee", icon: Coffee, emoji: "☕", label: "Find your favorite coffee spot", cta: "Browse", to: "/places?tab=dining" },
  { key: "event", icon: PartyPopper, emoji: "🎉", label: "Attend a student event", cta: "See events", to: "/places?tab=athletics" },
  { key: "people", icon: Users, emoji: "👋", label: "Meet people near you", cta: "Find friends", to: "/friends" },
  { key: "explore", icon: Map, emoji: "🗺️", label: "Explore campus", cta: "Open map", to: "/?view=full" },
  { key: "rewards", icon: Trophy, emoji: "🏆", label: "Earn your first rewards", cta: "Check in", to: "/rewards" },
];

export default function WelcomeChecklist() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(() => {
    api.get("/me/onboarding").then((r) => setState(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const go = async (step) => {
    // coffee/explore have no auto-signal — mark them done on tap.
    if (step.key === "coffee" || step.key === "explore") {
      setState((s) => s ? { ...s, steps: { ...s.steps, [step.key]: true }, done: s.done + (s.steps[step.key] ? 0 : 1) } : s);
      api.post("/me/onboarding/step", { key: step.key }).catch(() => {});
    }
    navigate(step.to);
  };

  const dismiss = async () => {
    setHidden(true);
    api.post("/me/onboarding/dismiss").catch(() => {});
  };

  if (!state || hidden || state.dismissed) return null;

  const allDone = state.complete;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, height: 0 }}
        className="mb-5 rounded-2xl border border-brand/25 bg-gradient-to-br from-brand/[0.06] to-brand/[0.02] overflow-hidden"
        data-testid="welcome-checklist"
      >
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-1.5 mono text-[11px] tracking-[0.2em] uppercase text-brand mb-1">
                <Sparkles size={13} strokeWidth={1.8} /> Welcome to campus
              </div>
              <h2 className="font-display text-xl md:text-2xl font-semibold tracking-tight">
                {allDone ? "You're all set — nice work! 🎉" : "5 things to do this week"}
              </h2>
            </div>
            <button data-testid="welcome-dismiss" onClick={dismiss}
              aria-label="Dismiss welcome checklist"
              className="p-2 rounded-md text-muted-foreground hover:bg-white/60 transition-colors shrink-0">
              <X size={18} strokeWidth={1.6} />
            </button>
          </div>

          {/* Progress */}
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 h-2 rounded-full bg-white/70 overflow-hidden">
              <motion.div className="h-full rounded-full bg-brand"
                initial={false} animate={{ width: `${(state.done / state.total) * 100}%` }}
                transition={{ type: "spring", stiffness: 200, damping: 26 }} />
            </div>
            <span className="mono text-xs font-bold text-brand shrink-0" data-testid="welcome-progress">
              {state.done}/{state.total}
            </span>
          </div>

          {/* Steps */}
          <div className="mt-4 grid gap-2">
            {STEPS.map((step) => {
              const done = !!state.steps[step.key];
              return (
                <button
                  key={step.key}
                  data-testid={`welcome-step-${step.key}`}
                  onClick={() => go(step)}
                  className={`group flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                    done ? "bg-white/50 border-transparent" : "bg-white border-border hover:border-brand"
                  }`}
                >
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                    done ? "bg-brand text-white" : "bg-brand/10 text-brand"
                  }`}>
                    {done ? <Check size={18} strokeWidth={2.4} /> : <step.icon size={17} strokeWidth={1.7} />}
                  </span>
                  <span className={`flex-1 text-sm font-semibold ${done ? "text-muted-foreground line-through" : ""}`}>
                    {step.emoji} {step.label}
                  </span>
                  {!done && (
                    <span className="flex items-center gap-0.5 text-xs font-semibold text-brand shrink-0">
                      {step.cta} <ChevronRight size={14} strokeWidth={2} className="group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {allDone && (
            <button data-testid="welcome-done-dismiss" onClick={dismiss}
              className="mt-4 w-full bg-brand text-white rounded-lg py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity">
              Let's go
            </button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
